import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import useFetch from "../lib/useFetch";
import { IAddress } from "../types/seed";

interface Credit {
  last_update: string;
  total_credit: number;
}

export interface PUProfile {
  id: number;
  email: string;
  name: string;
  address: string;
  address_obj: IAddress | null;
  phone: string;
  credit: { [key: string]: Credit };
  password?: string;
}

export interface CreditTransaction {
  id: number;
  amount: string;
  created_at: string;
  description: string;
  source_type: string;
  source_type_display: string;
  seed_name: string;
  seed_slug: string;
}

export interface CreditTransactionsResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: CreditTransaction[];
}

type OrderStatus = "CANCELED" | "ORDERED" | "PAID" | "FINISHED";

export interface PUOrder {
  address: string;
  created_at: string;
  credit_applied: number;
  id: number;
  items_count: number;
  shipping_fee: number;
  status: OrderStatus;
  subtotal: number;
  total: number;
}

export interface PUOrdersResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: PUOrder[];
}

const API = process.env.REACT_APP_API;

const usePerfilPU = () => {
  const fetch = useFetch();
  const profileQueryFn = () => {
    return fetch(`${API}/api/feira_online/urban_partner`);
  };

  return useQuery({
    queryKey: ["urban_partner"],
    queryFn: profileQueryFn,
    retry: (failureCount, error: any) => {
      if (error?.status === 401) {
        return false;
      }

      return failureCount < 3;
    },
  });
};

export const useCreditTransactions = ({ seedSlug }: { seedSlug?: string }) => {
  const fetch = useFetch();

  const queryFn = () => {
    if (!seedSlug) {
      return { data: undefined, isLoading: false, error: null };
    }

    const url = `${API}/api/feira_online/seeds/${seedSlug}/credit_transactions`;
    return fetch(url);
  };

  return useQuery({
    queryKey: ["credit-transactions", seedSlug],
    queryFn,
    staleTime: 1000 * 60 * 5, // 5 minutos
  });
};

export const useUpdatePerfilPU = () => {
  const fetch = useFetch();
  const queryClient = useQueryClient();

  const mutationFn = async (profile: Partial<PUProfile>) => {
    return fetch(`${API}/api/feira_online/urban_partner`, {
      method: "PATCH",
      body: profile,
    });
  };

  return useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries(["urban_partner"]);
    },
  });
};

export default usePerfilPU;
